import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { catchError, finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";

// NG-ZORRO Imports
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';

import { DownloadTrackerDto, EmailQueueDto, EmailQueueSearchDto, EmailQueueStatusEnum, PermissionEnum, ProductDto, ProductPackageDto } from "../../../dto";
import { HttpEmailService } from "../../../services/email/http-email.service";
import { HttpProductService } from "../../../services/product/http-product.service";
import { HttpPackageService } from "../../../services/package/http-package.service"; // Adjust import path
import { AuthService } from "../../../services/auth/auth.service";

@Component({
    selector: 'app-view-email',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule, 
        NzTableModule,
        NzButtonModule,
        NzIconModule,
        NzCardModule,
        NzGridModule,
        NzModalModule,
        NzFormModule,
        NzInputModule,
        NzSelectModule,
        NzDatePickerModule
    ],
    templateUrl: './view-email.component.html',
    styleUrls: ['./view-email.component.scss']
})
export class ViewEmailComponent implements OnInit {

    emails: EmailQueueDto[] = [];
    loading = false;
    
    // Search Form & Dropdown Data
    searchForm!: FormGroup;
    products: ProductDto[] = [];
    packages: ProductPackageDto[] = [];
    isProductsLoading = false;
    isPackagesLoading = false;

    // Modal & Lazy Loading State
    isModalVisible = false;
    downloadsLoading = false;
    selectedEmail: EmailQueueDto | null = null;
    selectedDownloads: DownloadTrackerDto[] = [];

    EmailQueueStatusEnum = EmailQueueStatusEnum;
    PermissionEnum = PermissionEnum;

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService, 
        private httpService: HttpEmailService,
        private productService: HttpProductService,
        private packageService: HttpPackageService,
        private router: Router,
        public authService: AuthService
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.fetchAllProducts();
        this.fetchEmails(); // Initial fetch with default dates
    }

    initForm() {
        this.resetForm();

        // Listen for product changes to fetch packages
        this.searchForm.get('product')?.valueChanges.subscribe((productId) => {
            this.searchForm.get('packages')?.setValue([]); // Clear selected packages
            if (productId) {
                this.loadPackages(productId);
            } else {
                this.packages = []; // Clear dropdown if product is cleared
            }
        });
    }

    fetchAllProducts() {
        this.isProductsLoading = true;
        this.productService.getAllProducts({})
            .pipe(finalize(() => this.isProductsLoading = false))
            .subscribe({
                next: (data) => this.products = data,
                error: (err) => this.toastr.error('Failed to load products')
            });
    }

    loadPackages(productId: number) {
        this.isPackagesLoading = true;
        this.packageService.getPackagesMinimal(productId)
            .pipe(finalize(() => this.isPackagesLoading = false))
            .subscribe({
                next: (data) => this.packages = data,
                error: (err) => this.toastr.error('Failed to load packages')
            });
    }

    onSearch() {
        this.fetchEmails();
    }

    resetForm() {
        const defaultStart = new Date();
        defaultStart.setDate(defaultStart.getDate() - 30);
        const defaultEnd = new Date();

        this.searchForm = this.fb.group({
            dateRange: [[defaultStart, defaultEnd]],
            product: [null],
            packages: [[]],
            customerName: [null],
            customerEmail: [null]
        });
    }

    fetchEmails() {
        this.loading = true;
        
        const formVals = this.searchForm.value;
        const [start, end] = formVals.dateRange || [null, null];

        const searchDto: EmailQueueSearchDto = {
            start: start,
            end: end,
            customerName: formVals.customerName || null,
            customerEmail: formVals.customerEmail || null,
            // Map selected package IDs back to DTO objects so backend logic `item.getId()` works
            packages: formVals.packages?.map((id: number) => ({ id } as ProductPackageDto)) || []
        };

        this.httpService.search(searchDto)
            .pipe(finalize(() => this.loading = false))
            .subscribe({
                next: (data) => this.emails = data,
                error: (err) => this.toastr.error('Failed to fetch emails')
            });
    }

    openDownloadsModal(email: EmailQueueDto) {
        this.selectedEmail = email;
        this.isModalVisible = true;
        this.downloadsLoading = true;
        this.selectedDownloads = [];

        this.httpService.getDownloads(email.id!)
            .pipe(finalize(() => this.downloadsLoading = false))
            .subscribe({
                next: (data) => this.selectedDownloads = data,
                error: (err) => this.toastr.error('Failed to fetch downloads')
            });
    }

    closeModal() {
        this.isModalVisible = false;
        this.selectedEmail = null;
    }

    composeEmail() {
        this.router.navigate(['/email']);
    }
}